/* eslint-disable indent, camelcase */

import { getEnv } from '../utils/environment';
import { getLocaleFromCountry, getGDPRStatusFromCountry } from '../utils/formatCountryData';
import { simpleFetch, simplePost } from './customFetch';
import {
  IPricingPreview,
  ICustomPricingObject,
  IUseCustomCheckoutPricing,
  IProspect,
  ICountry,
  IBillingMethods,
  ICustomizeOptions,
  PaymentType,
  EPayContext,
} from '../../interfaces';

interface Headers {
  'hcaptcha-token'?: string;
  'Content-Type'?: string;
  'FFF-Auth'?: string;
}

const PRICING_PREVIEW = '/api/2/preview';
const POST_REGISTER_V2 = '/api/2/register';
const CURRENCY = 'USD';
const SEASONAL_BOX_SOURCE = 'seasonalBox';
const INTRO_SOURCE = 'introProduct';

const buildPricingReviewObject = (
  customPricingObject: IUseCustomCheckoutPricing
): ICustomPricingObject => {
  const coupon = customPricingObject?.coupon;
  const sale_end_date = customPricingObject?.saleEndDate;
  const line_item_skus = customPricingObject?.lineItemSkus;
  const email = customPricingObject?.email;
  const cartId = customPricingObject?.cartId;

  return {
    ...(email && { email }),
    ...(coupon && { coupon: customPricingObject.coupon }),
    ...(cartId && { cart: { cart_id: cartId } }),
    subscription: {
      plan: customPricingObject.planCode,
      ...(coupon && { coupon: customPricingObject.coupon }),
      ...(sale_end_date && { sale_end_date: customPricingObject.saleEndDate }),
      ...(line_item_skus && {
        line_item_skus: customPricingObject.lineItemSkus,
      }),
    },
    shipping: {
      ship_country: customPricingObject.shipping.country,
      ship_state: customPricingObject.shipping.state,
      ship_zipcode: customPricingObject.shipping.zipCode,
    },
  };
};

const fetchPricingPreview = (
  customPricingObject: IUseCustomCheckoutPricing
): Promise<IPricingPreview> => {
  const pricingReviewObject: ICustomPricingObject = buildPricingReviewObject(customPricingObject);

  return simpleFetch({
    uri: '',
    options: { method: 'POST', body: JSON.stringify(pricingReviewObject) },
    baseUrl: getEnv().registerApiEndpoint + PRICING_PREVIEW,
  });
};

const postRegister = (
  recurly_token: string,
  prospect: IProspect,
  prospectNames: {
    firstName: string;
    lastName: string;
  },
  customize: ICustomizeOptions,
  paymentType: PaymentType,
  countries: ICountry[],
  conversion_fbp_tracking_parameters: string,
  conversion_fbc_tracking_parameters: string,
  hCaptchaToken: string,
  payContext: EPayContext,
  cartId?: number | undefined,
) => {
  const { email, password, planCode, shipCountry, shipState, shipZip, promoCode } = prospect;
  const { firstName, lastName } = prospectNames;
  const { campaignId, selectedProduct } = customize;
  const registerData = {
    billing_request: {
      payment_type: paymentType,
      preferred_locale: getLocaleFromCountry(countries, shipCountry),
      has_gdpr: getGDPRStatusFromCountry(countries, shipCountry),
    },
    campaign_id: campaignId || 0,
    customize_requests:
      campaignId && selectedProduct
        ? [
            {
              question_id: selectedProduct.questionId,
              option_id: selectedProduct.optionId,
              additional_item_option_ids: selectedProduct.additionalItemOptionIds,
            },
          ]
        : [],
    shipping_request: {
      ship_country: shipCountry,
      ship_state: shipState || '',
      ship_zipcode: shipZip,
    },
    subscription_request: {
      currency: CURRENCY,
      plan: planCode,
      coupon: promoCode,
      recurly_token_id: recurly_token,
    },
    user_request: {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
    },
    source: payContext === 'intro' ? INTRO_SOURCE : SEASONAL_BOX_SOURCE,
    conversion_tracking_parameters: {
      facebook: {
        fbc: conversion_fbc_tracking_parameters,
        fbp: conversion_fbp_tracking_parameters,
      },
    },
    ...(cartId && { cart_id: cartId }),
  };
  const headers: Headers = {};
  headers['hcaptcha-token'] = hCaptchaToken;
  headers['Content-Type'] = 'application/json';
  return simplePost(getEnv().registerApiEndpoint + POST_REGISTER_V2, registerData, { headers });
};

export { fetchPricingPreview, postRegister };
